/**
 * Header Component
 *
 * Barra superior do TMF igual ao PHP.
 * Menu + Logo à esquerda, busca no centro, usuário à direita.
 *
 * @since 2025-12-16
 * @updated 2025-12-16 - Adicionado seletor de idioma
 * @updated 2025-12-17 - Adicionado suporte a logo do theme e avatar do usuário via CDN
 * @updated 2025-12-17 - Integrado CheckinModal para widget de check-in
 * @epic INIT-0045-03, INIT-0046
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/theme';
import { useClientConfig } from '@/hooks/useClientConfig';
import { useAuth } from '@/contexts/AuthContext';
import { Dropdown } from '@/components/ui';
import { useLanguage, useTmfTranslation, type SupportedLanguageCode } from '@/i18n';
import { getPersonImageUrl, getCdnUrl, shouldUseCdn } from '@/config/cdn';
import { CheckinModal, CheckinScheduleModal } from '@/components/checkin';
import { useActiveCheckin } from '@/hooks/useCheckin';
import { DarkModeToggle } from './DarkModeToggle';
import { FragmentModeToggle } from './FragmentModeToggle';

// Fallback logo - deve estar em public/images/logos/
const FALLBACK_LOGO = '/images/logos/logo.png';

/**
 * Processa URL de logo do tema
 * Logos podem estar em:
 * - URL completa (http/https)
 * - Caminho CDN relativo (images/logos/...)
 * - Caminho relativo (/images/logos/...)
 * - Nome do arquivo apenas (logo.png) - usa CDN se configurado
 */
function getLogoUrl(logoPath: string | undefined): string {
  if (!logoPath) return FALLBACK_LOGO;

  // Se já é URL completa, retornar como está
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }

  // Se começa com images/ é caminho CDN, processar via CDN
  if (logoPath.startsWith('images/')) {
    if (shouldUseCdn()) {
      return getCdnUrl(logoPath);
    }
    // Fallback para ImageProxy Python API
    return `/api/v1/media/image-proxy?path=${encodeURIComponent(logoPath.replace('images/', ''))}`;
  }

  // Se começa com / é caminho relativo do servidor, retornar como está
  if (logoPath.startsWith('/')) {
    return logoPath;
  }

  // Se é só filename, usar CDN se configurado
  if (shouldUseCdn()) {
    return getCdnUrl(`images/logos/${logoPath}`);
  }

  // Fallback: usar caminho /images
  return `/images/logos/${logoPath}`;
}

/**
 * Processa URL do avatar do usuário
 * Avatar pode estar em:
 * - URL completa (http/https)
 * - Caminho relativo
 * - Nome do arquivo apenas - usa CDN/ImageProxy
 */
function getAvatarUrl(avatarPath: string | undefined): string | null {
  if (!avatarPath) return null;

  // Se já é URL completa, retornar como está
  if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
    return avatarPath;
  }

  // Usar função de pessoa do CDN
  return getPersonImageUrl(avatarPath, '150_150');
}

export function Header() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const { t } = useTmfTranslation('common');
  const { config: clientConfig } = useClientConfig();

  // Estado para controlar se a imagem do avatar falhou
  const [avatarError, setAvatarError] = useState(false);

  // Estado para controlar o modal de check-in
  const [checkinModalOpen, setCheckinModalOpen] = useState(false);

  // Estado para controlar o modal de agenda de check-ins
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  // Hook para verificar se há check-in ativo
  const { data: activeCheckin, loading: checkinLoading } = useActiveCheckin(user?.id || null);

  // Hook de idiomas (lista + idioma atual + setter) — usado para inline submenu
  const { language, setLanguage, languages } = useLanguage();

  // Logo: Hub config tem prioridade sobre theme
  const logoSrc = clientConfig?.logoUrl
    ? getLogoUrl(clientConfig.logoUrl)
    : getLogoUrl(theme.logos?.principal);

  // Avatar do usuário (processada via CDN/ImageProxy)
  const avatarSrc = getAvatarUrl(user?.avatar);

  // Resetar estado de erro quando o avatar mudar
  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar]);

  // Mostrar avatar se tiver URL e não tiver falhado
  const showAvatar = avatarSrc && !avatarError;

  // Verificar se usuário é Super Admin (qualquer variação de role)
  const isSadin = user?.roles?.some((role) =>
    ['admin', 'superadmin', 'ADMIN', 'SUPERADMIN', 'administrator', 'Sadin', 'sadin'].includes(role)
  ) ?? false;

  // Itens de idioma inline (evita submenu cortado pela borda — TMF9-54373).
  // Cada idioma vira um item próprio do menu, com check no idioma ativo.
  // O Radix DropdownMenuContent tem `overflow-hidden`, então um submenu CSS-only
  // (group-hover absolute) ficaria preso dentro da caixa do menu pai.
  const languageItems = languages.length > 1
    ? [
        {
          key: 'language-label',
          custom: true as const,
          label: (
            <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('Language')}
            </span>
          ),
        },
        ...languages.map((lang) => ({
          key: `lang-${lang.code}`,
          label: (
            <span className="flex items-center gap-2">
              <span className="text-base">{lang.flag}</span>
              <span className="flex-1">{lang.name}</span>
              {language === lang.code && (
                <i className="bi bi-check-lg text-[var(--brand-primary)]" aria-hidden="true" />
              )}
            </span>
          ),
          onClick: () => {
            void setLanguage(lang.code as SupportedLanguageCode);
          },
        })),
      ]
    : [];

  const userMenuItems = [
    { key: 'profile', label: t('My Data'), onClick: () => navigate('/profile') },
    { key: 'divider-1', divider: true as const },
    {
      key: 'theme-mode',
      custom: true as const,
      label: (
        <DarkModeToggle
          label={t('Appearance')}
          showLabel={true}
          size="sm"
        />
      ),
    },
    ...(languageItems.length > 0
      ? [{ key: 'divider-lang', divider: true as const }, ...languageItems]
      : []),
    // Fragment Mode Toggle - apenas para Sadin
    ...(isSadin ? [{
      key: 'fragment-mode',
      custom: true as const,
      label: <FragmentModeToggle />,
    }] : []),
    { key: 'divider-2', divider: true as const },
    { key: 'logout', label: t('Logout'), onClick: logout, danger: true },
  ];

  return (
    <header
      // [TMF9-55435] Borda inferior removida. Separação visual mantida via shadow-sm
      // (sombra suave que funciona em light e dark sem criar linha "dura" entre header e conteúdo).
      className="shadow-sm transition-colors duration-200"
      style={{
        backgroundColor: 'var(--topbar)',
        color: 'var(--topbar-foreground)',
      }}
    >
      <div className="flex items-center justify-between px-4 h-16">
        {/* Left: Logo */}
        <div className="flex items-center">
          <Link to="/">
            <img
              key={logoSrc}
              src={logoSrc}
              alt={theme.name || 'TMF'}
              className="h-10 w-auto rounded-[7px]"
              onError={(e) => {
                // Se falhar, usar fallback
                const img = e.target as HTMLImageElement;
                if (img.src !== FALLBACK_LOGO) {
                  img.src = FALLBACK_LOGO;
                }
              }}
            />
          </Link>
        </div>

        {/* Center: Search/Checkin Widget - cores derivadas do topbar */}
        <div className="flex-1 max-w-xl mx-8 hidden md:flex items-center gap-2">
          {/* Botão principal de check-in */}
          <button
            type="button"
            onClick={() => setCheckinModalOpen(true)}
            className="flex-1 flex items-center rounded-full px-4 py-2 transition-colors group"
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'var(--topbar-foreground)',
            }}
          >
            {/* GPS icon with active indicator */}
            <div className="relative mr-3">
              <i
                className={`bi bi-geo-alt ${activeCheckin ? 'text-green-500 dark:text-green-400' : ''}`}
                style={!activeCheckin ? { color: 'var(--topbar-foreground)', opacity: 0.7 } : undefined}
              />
              {activeCheckin && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              )}
            </div>

            {/* Text */}
            <span
              className="flex-1 text-left text-sm"
              style={{ color: 'var(--topbar-foreground)', opacity: activeCheckin ? 1 : 0.8 }}
            >
              {checkinLoading ? (
                'Carregando...'
              ) : activeCheckin ? (
                <span className="text-green-600 dark:text-green-400 font-medium">
                  Check-in ativo: {activeCheckin.resource?.resource_name || 'Local'}
                </span>
              ) : (
                'Clique e faça check-in'
              )}
            </span>

            {/* Action icons */}
            <div className="flex items-center gap-1 ml-2">
              {activeCheckin ? (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                  Ativo
                </span>
              ) : (
                <>
                  <i className="bi bi-search" style={{ color: 'var(--topbar-foreground)', opacity: 0.6 }} />
                  <i className="bi bi-chevron-right" style={{ color: 'var(--topbar-foreground)', opacity: 0.6 }} />
                </>
              )}
            </div>
          </button>

          {/* Botão de agenda/calendário */}
          <button
            type="button"
            onClick={() => setScheduleModalOpen(true)}
            className="flex items-center justify-center w-10 h-10 rounded-full transition-colors group"
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
            title="Agenda de Visitas"
          >
            <i className="bi bi-calendar3" style={{ color: 'var(--topbar-foreground)', opacity: 0.7 }} />
          </button>
        </div>

        {/* Right: User Menu */}
        <div className="flex items-center">
          <Dropdown
          trigger={
            <button
              data-testid="user-menu"
              className="flex items-center gap-3 p-2 rounded-lg transition"
              style={{ color: 'var(--topbar-foreground)' }}
            >
              <div className="w-9 h-9 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                {showAvatar ? (
                  <img
                    src={avatarSrc}
                    alt={user?.name || 'Usuário'}
                    className="w-full h-full object-cover"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: theme?.blocks?.block2?.background || '#e35b00' }}
                  >
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium" style={{ color: 'var(--topbar-foreground)' }}>{user?.name || 'Usuário'}</p>
                <p className="text-xs" style={{ color: 'var(--topbar-foreground)', opacity: 0.7 }}>Menu do Usuário</p>
              </div>
              <i className="bi bi-chevron-down text-xs" style={{ color: 'var(--topbar-foreground)', opacity: 0.7 }} />
            </button>
          }
          items={userMenuItems}
          placement="bottom-end"
        />
        </div>
      </div>

      {/* Check-in Modal */}
      <CheckinModal
        isOpen={checkinModalOpen}
        onClose={() => setCheckinModalOpen(false)}
      />

      {/* Agenda de Visitas Modal */}
      <CheckinScheduleModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
      />
    </header>
  );
}
